<template>
  <div class="prelim-content">
    <template
      v-if="currentStepIndex === LEASING_PRELIM_FIXED_STEPS.AMOUNT_AND_TERM"
    >
      <LeasingPrescoreAmountAndTerm
        :filter-settings="filterSettings"
        :active-purchase="activePurchase"
      />
    </template>

    <template v-if="loaded">
      <template
        v-if="currentStepIndex === LEASING_PRELIM_FIXED_STEPS.TYPE_SELECT"
      >
        <LeasingPrescoreTypeSelector />
      </template>

      <template
        v-if="
          isPsd2 && currentStepIndex === LEASING_PRELIM_PSD2_FIXED_STEPS.SYNC
        "
      >
        <LeasingPrescorePsd2Sync />
      </template>

      <template v-if="currentStep">
        <p class="has-text-weight-bold is-size-4">
          {{ currentStep.name }}
        </p>

        <validation-observer ref="prelimObserver">
          <div v-for="question in currentStep.questions" :key="question.id">
            <LeasingPrescoreRadioQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.RADIO"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />

            <LeasingPrescoreSelectQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.SELECT"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />

            <LeasingPrescoreInputQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.INPUT"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />

            <LeasingPrescoreDatePickerQuestion
              v-if="question.type === SIMPLE_FIELD_TYPE.DATE_PICKER"
              :field="question"
              :visible="isVisible(question.identifier)"
              :key="question.identifier"
            />
          </div>
        </validation-observer>
      </template>
    </template>

    <LoadingSkeleton v-else />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  LEASING_PRELIM_FIXED_STEPS,
  LEASING_PRELIM_PSD2_FIXED_STEPS,
  SIMPLE_FIELD_TYPE,
} from "../utils/const";
import LoadingSkeleton from "../components/LoadingSkeleton";
import LeasingPrescoreTypeSelector from "@/components/leasing-prescore/LeasingPrescoreTypeSelector";
import LeasingPrescorePsd2Sync from "@/components/leasing-prescore/LeasingPrescorePsd2Sync";

export default {
  name: "LeasingPrescore",
  title: "af:page.preliminary_assessment.title",
  components: {
    LeasingPrescoreTypeSelector,
    LoadingSkeleton,
    LeasingPrescorePsd2Sync,
    LeasingPrescoreAmountAndTerm: () =>
      import("@/components/leasing-prescore/LeasingPrescoreAmountAndTerm.vue"),
    LeasingPrescoreRadioQuestion: () =>
      import("@/components/leasing-prescore/LeasingPrescoreRadioQuestion.vue"),
    LeasingPrescoreSelectQuestion: () =>
      import("@/components/leasing-prescore/LeasingPrescoreSelectQuestion.vue"),
    LeasingPrescoreInputQuestion: () =>
      import("@/components/leasing-prescore/LeasingPrescoreInputQuestion.vue"),
    LeasingPrescoreDatePickerQuestion: () =>
      import(
        "@/components/leasing-prescore/LeasingPrescoreDatePickerQuestion.vue"
      ),
  },
  data() {
    return {
      SIMPLE_FIELD_TYPE,
      LEASING_PRELIM_FIXED_STEPS,
      LEASING_PRELIM_PSD2_FIXED_STEPS,
      loaded: false,
    };
  },
  async created() {
    try {
      await this.fetchPrescoreByPublicId(this.publicId);
    } catch (error) {
      console.error("Error fetching prescore by public ID:", error);
    }

    try {
      await this.$store.dispatch("leasingPrescore/fetchLatest");
    } catch {}

    if (this.type) {
      await this.$store.dispatch("leasingPrescore/fetchSteps", this.type);
    }

    await this.$store.dispatch("purchase/fetchActivePurchase");

    await this.fetchFilterSettings(this.dealerId);

    // Prefill amount and duration if not already set TODO!
    // await this.prefillAmount();
    // await this.prefillDuration();

    // console.log(this.getAmountFromStore("amount"));
    this.storeData("amount", this.leasingPrescore.amount);
    this.storeData("duration", this.leasingPrescore.duration);
    this.storeData("downPayment", this.leasingPrescore.downPayment);

    this.loaded = true;
  },
  watch: {
    currentStep: {
      immediate: true,
      handler: function () {
        this.$nextTick(() => {
          this.$nextTick(() => {
            let observer = this.$refs.prelimObserver;
            if (observer) {
              observer.reset();
            }
          });
        });
      },
    },
  },
  computed: {
    ...mapGetters({
      steps: "leasingPrescore/steps",
      step: "leasingPrescore/step",
      currentStepIndex: "leasingPrescore/currentStepIndex",
      isVisible: "leasingPrescore/isVisible",
      isPsd2: "leasingPrescore/isPsd2",
      type: "leasingPrescore/getType",
      filterSettings: "leasing/filterSettings",
      dealerId: "leasing/dealerId",
      publicId: "leasing/publicId",
      activePurchase: "purchase/getActivePurchase",
      leasingPrescore: "leasingPrescore/getPrescore",
    }),
    currentStep() {
      return this.step(this.currentStepIndex);
    },
    isLeasing() {
      return /^\/leasing.*/i.test(this.$route.path);
    },
  },
  methods: {
    ...mapActions({
      fetchFilterSettings: "leasing/fetchFilterSettings",
      fetchPrescoreByPublicId: "leasingPrescore/fetchPrescoreByPublicId",
    }),
    getAmountFromStore(questionId) {
      return this.$store.getters["leasingPrescore/answer"](questionId);
    },
    storeData(questionId, value) {
      this.$store.dispatch("leasingPrescore/addData", {
        question: questionId,
        answer: value,
      });
    },
    async prefillAmount() {
      const storedAmount = this.getAmountFromStore("amount");

      let initialAmount = null;
      if (!storedAmount) {
        initialAmount = this.filterSettings.amountMin;
      } else {
        initialAmount = storedAmount;
      }

      this.storeData("amount", initialAmount);
    },
    async prefillDuration() {
      const storedDuration = this.getAmountFromStore("duration");

      let initialDuration = null;
      if (!storedDuration) {
        initialDuration = this.filterSettings.durationMin;
      } else {
        initialDuration = storedDuration;
      }

      this.storeData("duration", initialDuration);
    },
  },
};
</script>

<style scoped lang="scss">
.prelim-content {
  padding: 20px;
}
</style>
